import { useEffect, useRef } from "react";
import { Dialog } from "./Dialog";
import { useAnalytics } from "../helpers/hooks/use-analytics";

const RECOMMENDATIONS_URL = "https://docsend.com/view/uccps9yxchq2wwt7";

interface MinFeeEarlyWarningModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * One-time "good news" dialog shown on the user's first visit after the
 * min-fee early-warning email goes out (see useMinFeeEarlyWarning for the
 * gating rules). Every close path (Esc, backdrop, X, both buttons) routes
 * through onClose, which writes the show-once latch.
 */
export function MinFeeEarlyWarningModal({
  open,
  onClose,
}: MinFeeEarlyWarningModalProps) {
  const { trackEvent } = useAnalytics();
  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      trackEvent("min_fee_early_warning_shown");
      hasTrackedOpen.current = true;
    }
  }, [open, trackEvent]);

  if (!open) return null;

  const handleDismiss = (source: string) => {
    trackEvent("min_fee_early_warning_dismissed", { source });
    onClose();
  };

  const handleViewRecommendations = () => {
    window.open(RECOMMENDATIONS_URL, "_blank", "noopener");
    handleDismiss("view_recommendations");
  };

  return (
    <Dialog
      variant="center"
      size="sm"
      title="Good news: Autumn is driving demand"
      onClose={() => handleDismiss("close")}
      primaryAction={{
        label: "View recommendations",
        onClick: handleViewRecommendations,
        dataAttr: "min-fee-early-warning-view-recommendations",
      }}
      secondaryAction={{
        label: "Dismiss",
        onClick: () => handleDismiss("dismiss"),
        dataAttr: "min-fee-early-warning-dismiss",
      }}
    >
      <div className="space-y-3">
        <p className="text-body leading-relaxed text-content-primary">
          Autumn is bringing many very engaged travelers to your website.
        </p>
        <p className="text-subhead leading-relaxed text-content-secondary">
          We spotted an opportunity to help convert more of those visitors
          into bookings and sent you a few recommendations by email.
        </p>
      </div>
    </Dialog>
  );
}
